import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';

import { DefaultBadge } from '../../atoms/DefaultBadge';

export interface SortableTabProps {
  /** Drag id — the report code, or DEFAULT_TAB_ID for the default tab. */
  id: string;
  label: string;
  isActive: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  /** When set, renders the leading "D" badge (default tab) in this colour variant. */
  badgeVariant?: 'system' | 'report';
  /** Accessible label + tooltip for the badge. */
  badgeLabel?: string;
  /** When set, the tab is closeable (report tab) and shows a close button on hover. */
  onClose?: () => void;
  /** Accessible name for the close button. */
  closeLabel?: string;
}

export const SortableTab: React.FC<SortableTabProps> = ({
  id,
  label,
  isActive,
  onSelect,
  onContextMenu,
  badgeVariant,
  badgeLabel,
  onClose,
  closeLabel,
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      // Explicit props after the dnd-kit spreads so our tab semantics win over
      // its default role="button"/tabIndex.
      role="tab"
      aria-selected={isActive}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      onContextMenu={onContextMenu}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={{
        position: 'relative',
        px: 2,
        py: 1,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: '2px solid',
        borderColor: isActive ? theme.palette.v4.accent : 'transparent',
        color: isActive ? theme.palette.v4.accent : 'text.secondary',
        fontWeight: isActive ? 600 : 400,
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        opacity: isDragging ? 0.5 : 1,
        // Chrome/Firefox-style tab: the active tab sits on a subtle grey tint
        // with rounded top corners. Hovering an inactive tab previews that look
        // at ~75% strength (a lighter version of the active tint).
        borderTopLeftRadius: '6px',
        borderTopRightRadius: '6px',
        bgcolor: isActive ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
        '&:hover': {
          color: theme.palette.v4.accent,
          ...(isActive ? {} : { bgcolor: alpha(theme.palette.text.primary, 0.045) }),
        },
        // Thin vertical divider between inactive tabs — hidden on the active tab,
        // the tab immediately before it, and the last tab (so it never brackets
        // the active tab or dangles before the "+" button).
        '&::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '25%',
          height: '50%',
          width: '1px',
          bgcolor: theme.palette.v4.border,
        },
        '&[aria-selected="true"]::after': { display: 'none' },
        '&:has(+ [aria-selected="true"])::after': { display: 'none' },
        '&:last-of-type::after': { display: 'none' },
      }}
    >
      {badgeVariant && <DefaultBadge variant={badgeVariant} label={badgeLabel} />}
      <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
        {label}
      </Typography>
      {onClose && (
        <IconButton
          size="small"
          aria-label={closeLabel}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          // Always visible on closeable tabs; tight padding, and alignSelf keeps
          // it centered on the title's line regardless of the tab's row height.
          sx={{ p: 0.125, ml: 0.25, alignSelf: 'center', color: 'inherit' }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
      )}
    </Box>
  );
};
